import React, { useEffect, useMemo, useState } from 'react';
import { Card, Badge, Button, Spinner } from 'react-bootstrap';
import { HorizontalBar } from 'react-chartjs-2';
import { updateBranches } from '../../../api/updatesService';

// Mantém as funções utilitárias locais para encapsulamento, mas pode exportar se necessário
const getChartOptions = () => {
  const isDarkTheme = document.body.classList.contains('dark-theme');
  return {
    scales: {
      y: [{
        ticks: { beginAtZero: true, fontColor: isDarkTheme ? '#adb5bd' : '#666', fontStyle: 'bold', precision: 0 },
        gridLines: { display: false },
        maxBarThickness: 30
      }],
      x: [{
        ticks: { beginAtZero: true, fontColor: isDarkTheme ? '#adb5bd' : '#666', padding: 10, maxRotation: 0 },
        gridLines: { color: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)', zeroLineColor: isDarkTheme ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)' }
      }]
    },
    legend: { display: false },
    maintainAspectRatio: false,
    responsive: true,
    tooltips: {
      backgroundColor: isDarkTheme ? 'rgba(30, 30, 30, 0.9)' : 'rgba(255, 255, 255, 0.9)',
      titleFontColor: isDarkTheme ? '#f8f9fa' : '#333',
      bodyFontColor: isDarkTheme ? '#adb5bd' : '#666',
      borderColor: isDarkTheme ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
      borderWidth: 1,
      displayColors: true,
      cornerRadius: 6,
      caretSize: 6,
      titleFontStyle: 'bold',
      callbacks: {
        title: (items) => `Versão: ${items[0].label}`,
        label: (item) => `Quantidade: ${item.raw || item.value}`
      }
    }
  };
};

const createBarGradient = (ctx, isDark) => {
  const gradient = ctx.createLinearGradient(0, 0, 300, 0);
  const c1 = isDark ? '#2a7a37' : '#3ab34a';
  const c2 = isDark ? '#2a7a3799' : '#3ab34a99';
  gradient.addColorStop(0, c1);
  gradient.addColorStop(1, c2);
  return gradient;
};

const VersionDistributionChart = ({ eligibleEquipments, latestVersion, latestVersions = [] }) => {
  const [options, setOptions] = useState(getChartOptions());
  const [loading, setLoading] = useState(false);

  const handleUpdateBranches = async () => {
    setLoading(true);
    try {
      await updateBranches();
      window.location.reload()
    } catch (error) {
      alert('Erro ao atualizar as branches:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  // Atualiza tema
  useEffect(() => {
    const handleThemeChange = () => setOptions(getChartOptions());
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((m) => { if (m.attributeName === 'class') handleThemeChange(); });
    });
    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const versionCounts = useMemo(() => {
    const counts = {};
    eligibleEquipments.forEach((eq) => {
      const v = eq.currentVersion || 'Desconhecida';
      counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  }, [eligibleEquipments]);

  const chartLabels = useMemo(() => {
    const semverCompare = (a, b) => {
      if (a === 'Desconhecida') return 1;
      if (b === 'Desconhecida') return -1;
      const partsA = a.split('.').map(Number);
      const partsB = b.split('.').map(Number);
      const len = Math.max(partsA.length, partsB.length);
      for (let i = 0; i < len; i++) {
        const valA = partsA[i] || 0;
        const valB = partsB[i] || 0;
        if (valA !== valB) {
          return valB - valA;
        }
      }
      return 0;
    };
    return Object.keys(versionCounts).sort(semverCompare);
  }, [versionCounts]);

  const chartData = useMemo(() => ({
    labels: chartLabels,
    datasets: [
      {
        label: 'Quantidade',
        data: chartLabels.map((l) => versionCounts[l] || 0),
        backgroundColor: (ctx) => {
          const chart = ctx.chart;
          const { ctx: canvasCtx } = chart;
          const isDark = document.body.classList.contains('dark-theme');
          return createBarGradient(canvasCtx, isDark);
        },
        borderWidth: 0,
        barThickness: 'flex'
      }
    ]
  }), [chartLabels, versionCounts]);

  return (
    <Card className="mb-3 mt-2" style={{ minHeight: 'calc(100vh - 120px)' }}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="fw-bold" style={{ color: 'var(--brand-dark-green)', margin: 0 }}>
            Distribuição de versões SW.MT (SIMT)
          </h4>
          <Button onClick={handleUpdateBranches} disabled={loading} size="sm">
            {loading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Atualizar Branches'}
          </Button>
        </div>
        {(latestVersions.length > 0 || latestVersion) && (
          <div className="mb-2 d-flex flex-wrap gap-2 align-items-center" style={{ gap: '8px' }}>
            {latestVersions.slice(0, 2).map((v, idx) => (
              <Badge key={v} bg={idx === 0 ? 'success' : 'secondary'}>
                {idx === 0 ? 'Mais recente' : 'Anterior'}: {v}
              </Badge>
            ))}
            {(!latestVersions.length && latestVersion) && (
              <Badge bg="success">Última versão detectada: {latestVersion}</Badge>
            )}
          </div>
        )}
        <div style={{ height: 'calc(100vh - 240px)' }}>
          <HorizontalBar data={chartData} options={options} />
        </div>
      </Card.Body>
    </Card>
  );
};

export default VersionDistributionChart;